import { IBidderGroup } from '~/src/dash/api/bidder-group';
import { IAdserverCampaign } from '~/src/dash/api/campaign/campaign';
import { ICompany } from '~/src/dash/api/company';
import {
  IAdserverCreative,
  IAdserverGlobalMediafile,
  IBidderLabel,
  IChannelGroup,
  IChannelLabel,
  IHeaderBidder,
  IUser,
  IUserFromUserToPublisherAssociation,
  IVariantMediafile,
} from '~/src/dash/api/console-api';
import { IDeal } from '~/src/dash/api/deals';
import { IDestination, IInventoryRoute } from '~/src/dash/api/inventory_routes';
import { IMonitor } from '~/src/dash/api/monitors/monitors_models';
import { IAdserverOrder } from '~/src/dash/api/order';
import { IMenuList } from '~/src/dash/types/menuList';
import { isDemoAccount } from '~/src/dash/utils/ui';

export const obfuscateCampaignName = (
  campaign: IAdserverCampaign,
  username: string,
  ifNotObfuscate: string,
): string => {
  return isDemoAccount(username) ? 'demo_line_item_' + campaign.ID : ifNotObfuscate;
};

export const obfuscateBidderName = (
  bidder: IHeaderBidder,
  username: string,
  ifNotObfuscate: string,
): string => {
  return isDemoAccount(username) ? 'demo_bidder_' + bidder.ID : ifNotObfuscate;
};

interface IWithID {
  ID: string | number;
}

export function obfuscateSiteName<Type extends IWithID>(
  site: Type,
  username: string,
  ifNotObfuscate: string,
): string {
  if (isDemoAccount(username)) {
    if (typeof site.ID === 'string') {
      return 'demo_site_' + site.ID;
    }
    return 'demo_site_' + site.ID.toString();
  }
  return ifNotObfuscate;
}

export const obfuscateChannelGroupName = (
  cg: IChannelGroup,
  username: string,
  ifNotObfuscate: string,
): string => {
  return isDemoAccount(username) ? 'demo_channel_group_' + cg.ID : ifNotObfuscate;
};

export const obfuscateBidderGroupName = (
  bg: IBidderGroup,
  username: string,
  ifNotObfuscate: string,
): string => {
  return isDemoAccount(username) ? 'demo_bidder_group_' + bg.ID : ifNotObfuscate;

  return ifNotObfuscate;
};

export const obfuscateUserName = (
  user: IUser,
  username: string,
  ifNotObfuscate: string,
): string => {
  return isDemoAccount(username) ? 'demo_user_' + user.ID : ifNotObfuscate;

  return ifNotObfuscate;
};

export const obfuscateCompanyName = (
  company: ICompany,
  username: string,
  ifNotObfuscate: string,
): string => {
  return obfuscate<ICompany>('company', company, username, ifNotObfuscate);
};

export const obfuscateOrderName = (
  order: IAdserverOrder,
  username: string,
  ifNotObfuscate: string,
): string => {
  return obfuscate<IAdserverOrder>('order', order, username, ifNotObfuscate);
};

export const obfuscateDealName = (
  deal: IDeal,
  username: string,
  ifNotObfuscate: string,
): string => {
  return obfuscate<IDeal>('deal', deal, username, ifNotObfuscate);
};

export const obfuscateVariant = (
  variant: IVariantMediafile,
  username: string,
  suffix: string,
  ifNotObfuscate: string,
): string => {
  const resource = !suffix ? 'variant' : `variant_${suffix}`;
  return obfuscate<IVariantMediafile>(resource, variant, username, ifNotObfuscate);
};

export const obfuscateGlobalMediafile = (
  mediafile: IAdserverGlobalMediafile,
  username: string,
  suffix: string,
  ifNotObfuscate: string,
): string => {
  const resource = !suffix ? 'mediafile' : `mediafile_${suffix}`;
  return obfuscate<IAdserverGlobalMediafile>(resource, mediafile, username, ifNotObfuscate);
};

export const obfuscateAdserverCreative = (
  adserverCreative: IAdserverCreative,
  username: string,
  ifNotObfuscate: string,
): string => {
  return obfuscate<IAdserverCreative>('creative', adserverCreative, username, ifNotObfuscate);
};

export const obfuscateAdserverCampaign = (
  adserverCampaign: IAdserverCampaign,
  username: string,
  ifNotObfuscate: string,
): string => {
  return obfuscate<IAdserverCampaign>('line_item', adserverCampaign, username, ifNotObfuscate);
};

export const obfuscateCampaignCreativeList = (
  campaignCreativeList: IMenuList,
  key: string,
  username: string,
): string => {
  return isDemoAccount(username)
    ? `demo_creative_${campaignCreativeList[key].ID}`
    : campaignCreativeList[key].title;
};

export const obfuscateCampaignCreative = (id: string): string => {
  return `demo_creative_${id}`;
};

export const obfuscateCampaignVast = (id: string): string => {
  return `demo_vast_${id}`;
};

export const obfuscateCampaignVastList = (
  campaignVastList: IMenuList,
  key: string,
  username: string,
): string => {
  return isDemoAccount(username)
    ? `demo_vast_${campaignVastList[key].ID}`
    : campaignVastList[key].title;
};

export const obfuscateUserInfo = (
  user: IUserFromUserToPublisherAssociation,
  username: string,
): IUserFromUserToPublisherAssociation => {
  if (isDemoAccount(username)) {
    user.Email = 'demo_user_' + user.UserID;
    user.FirstName = 'First';
    user.LastName = 'Last';
    return user;
  }
  return user;
};

// helper method to obfuscate names of properties for demo
function obfuscate<T>(resource: string, obj: T, username: string, defaultValue: string): string {
  // @ts-ignore
  return isDemoAccount(username) ? `demo_${resource}_${obj.ID}` : defaultValue;
}

export const obfuscateMonitorName = (
  monitor: IMonitor,
  username: string,
  ifNotObfuscate: string,
): string => {
  return isDemoAccount(username) ? 'demo_monitor_' + monitor.ID : ifNotObfuscate;
};

export const obfuscateInventoryRouteName = (
  inventoryRoute: IInventoryRoute,
  username: string,
  ifNotObfuscate: string,
): string => {
  return isDemoAccount(username) ? 'demo_inventory_route_' + inventoryRoute.ID : ifNotObfuscate;
};

export const obfuscateDestinations = (
  destination: IDestination,
  username: string,
  ifNotObfuscate: string,
): string => {
  return isDemoAccount(username) ? 'demo_Destination_' + destination.ID : ifNotObfuscate;
};

export const obfuscateChannelLabels = (
  ChannelLabel: IChannelLabel,
  username: string,
  ifNotObfuscate: string,
): string => {
  return isDemoAccount(username) ? 'demo_Channel_Label_' + ChannelLabel.ID : ifNotObfuscate;
};

export const obfuscateBidderLabels = (
  BidderLabel: IBidderLabel,
  username: string,
  ifNotObfuscate: string,
): string => {
  return isDemoAccount(username) ? 'demo_Bidder_Label_' + BidderLabel.ID : ifNotObfuscate;
};

export const obfuscateDataLabel = (
  username: string,
  label: string,
  prefix: string,
  suffix: string,
): string => {
  return isDemoAccount(username) ? prefix + '_' + suffix : label;
};
